import { StrictMode } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'

import { AppRoutes } from './app/routes'
import { Providers } from './app/providers'
import { ErrorBoundary } from './shared/ui/ErrorBoundary'
import './styles/global.css'
import './styles/tables.css'
import './styles/docs.css'

const container = document.getElementById('root')
if (!container) {
  throw new Error('Missing #root element')
}

/*
  A lazy route's chunk can be gone by the time somebody navigates to it: a tab opened
  before a deploy still names the previous build's hashed files, and the server no
  longer has them. Vite reports that as `vite:preloadError` (the chunk itself or one
  of its imports failed to load). Reloading fetches the new build's index.html and
  with it the chunk names that exist now. At most once a minute per tab, so a chunk
  that is really missing ends at the error boundary instead of a reload loop. Every
  lazy page shares this exposure (Policies since #1313, Federation, CavsdbServer,
  Verify, the docs and the changelog), so it is handled here once.
*/
window.addEventListener('vite:preloadError', (event) => {
  const key = 'archis:chunk-reload'
  try {
    const last = Number(sessionStorage.getItem(key) ?? 0)
    if (Date.now() - last < 60_000) return
    sessionStorage.setItem(key, String(Date.now()))
  } catch {
    return // no storage, so no loop guard: let the error boundary show it
  }
  event.preventDefault()
  window.location.reload()
})

const app = (
  <StrictMode>
    {/*
      The boundary is outside Providers on purpose: a bad or missing Firebase config
      throws while Providers initialises, and that is exactly the failure a person
      most needs a readable message for.
    */}
    <ErrorBoundary>
      <Providers>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </Providers>
    </ErrorBoundary>
  </StrictMode>
)

if (container.hasChildNodes()) {
  hydrateRoot(container, app)
} else {
  createRoot(container).render(app)
}
